import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { MdAdd, MdClose } from "react-icons/md";
import API_BASE_URL from "../../../utils/apiBase";
import { useProjects } from "../../../utils/projects";
import { listChannelPartners, registerChannelPartner } from "../../../utils/channelPartners";
import ProjectLogo from "../../../Components/ProjectLogo/ProjectLogo";
import { Empty, PersonName, Pill, ProjectName } from "../cells";
import "../projects/ProjectsView.css";
import "../rms/RmView.css";
import "./BrokersView.css";

const PAGE = 50;

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

// 9876543210 -> "98765 43210"
const formatPhone = (value) => {
  const digits = String(value ?? "");
  return digits.length === 10 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;
};

// Sales list entries are "Sales person - Team leader"; the registration form
// stores only the person (the part before the first "-"). Same here, trimmed.
const salesPerson = (entry = "") => entry.split("-")[0].trim();

const EMPTY_FORM = { name: "", phoneNumber: "", companyName: "", meetingWith: "", placeOfVisit: "", date: today() };

// Channel partners (brokers). The admin can see every registered partner and
// register a new one directly from the dashboard — it goes through the same
// endpoint as the Channel Partner form, credited to the admin.
const BrokersView = ({ searchQuery = "" }) => {
  const { projects } = useProjects();
  const [project, setProject] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [freshId, setFreshId] = useState(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [salesNames, setSalesNames] = useState([]);

  // Debounce the dashboard search so typing doesn't fire a request per key
  const [query, setQuery] = useState(searchQuery.trim());
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const requestId = useRef(0);
  const load = useCallback(
    async ({ append = false } = {}) => {
      const id = ++requestId.current;
      setLoading(true);
      try {
        const data = await listChannelPartners({
          q: query,
          project,
          skip: append ? items.length : 0,
          limit: PAGE,
        });
        if (id !== requestId.current) return; // a newer search replaced this one
        setItems((current) => (append ? [...current, ...data.items] : data.items));
        setTotal(data.total);
        setLoadError("");
      } catch (error) {
        if (id === requestId.current) setLoadError(error.message);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    // items.length is read only when appending
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, project, items.length]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, project]);

  // Meeting-with choices, same source as the registration forms
  useEffect(() => {
    if (!open || salesNames.length) return;
    fetch(`${API_BASE_URL}/api/v1/sales`)
      .then((r) => r.json())
      .then((body) => setSalesNames(body?.data?.[0]?.names || []))
      .catch(() => setSalesNames([]));
  }, [open, salesNames.length]);

  const set = (field) => (event) => {
    let { value } = event.target;
    if (field === "phoneNumber") value = value.replace(/\D/g, "").slice(0, 10);
    setForm((f) => ({ ...f, [field]: value }));
  };

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, date: today(), placeOfVisit: project || "" });
    setOpen(true);
  };
  const close = () => !saving && setOpen(false);

  const formValid =
    form.name.trim().length >= 2 &&
    form.phoneNumber.length === 10 &&
    form.companyName.trim().length >= 2 &&
    form.placeOfVisit &&
    form.date;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formValid) return;
    setSaving(true);
    try {
      const result = await registerChannelPartner({
        name: form.name.trim(),
        phoneNumber: form.phoneNumber,
        companyName: form.companyName.trim(),
        meetingWith: form.meetingWith,
        placeOfVisit: form.placeOfVisit,
        date: form.date,
        createdBy: localStorage.getItem("username"),
      });
      toast.success(result.message);
      setFreshId(result.data?._id || null);
      setOpen(false);
      // Newest first, so it shows at the top (clearing the project filter reloads)
      if (project) setProject("");
      else load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const filterProjects = projects.map((p) => p.name);

  return (
    <div className="reservation-box brokers-view">
      <div className="top projects-header">
        <div className="projects-heading">
          <h2>Channel partners</h2>
          <p>
            {loading && !items.length
              ? "Loading…"
              : `${total.toLocaleString("en-IN")} ${query || project ? "matching" : "brokers registered"}`}
          </p>
        </div>
        <button type="button" className="pv-btn pv-btn--primary" onClick={openAdd}>
          <MdAdd /> Add channel partner
        </button>
      </div>

      <div className="cp-filter" role="group" aria-label="Filter by project">
        {["", ...filterProjects].map((name) => (
          <button
            key={name || "all"}
            type="button"
            className={`cp-filter-chip${project === name ? " is-active" : ""}`}
            aria-pressed={project === name}
            onClick={() => setProject(name)}
          >
            {name ? <ProjectLogo name={name} size={20} /> : null}
            {name || "All projects"}
          </button>
        ))}
      </div>

      <div className="table brokers-table">
        {loadError ? (
          <div className="projects-empty">
            <p>
              <strong>Couldn't load channel partners.</strong> {loadError}
            </p>
            <button type="button" className="pv-btn pv-btn--ghost" onClick={() => load()}>
              Try again
            </button>
          </div>
        ) : !loading && items.length === 0 ? (
          <div className="projects-empty">
            <p>
              <strong>No channel partners found</strong>
              {query ? ` for "${query}"` : ""}
              {project ? ` at ${project}` : ""}.
            </p>
            <small>Try another search or project.</small>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Partner</th>
                <th>Company</th>
                <th>Phone</th>
                <th>Project</th>
                <th>Meeting with</th>
                <th>Customers</th>
                <th>Registered by</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((cp) => (
                <tr key={cp.id} className={cp.id === freshId ? "is-fresh" : undefined}>
                  <td><PersonName name={cp.name} /></td>
                  <td><Pill tone="company">{cp.companyName}</Pill></td>
                  <td className="cell-phone">{formatPhone(cp.phoneNumber)}</td>
                  <td><ProjectName name={cp.placeOfVisit} /></td>
                  <td><Pill tone="person">{cp.meetingWith?.trim()}</Pill></td>
                  <td>
                    {cp.customers > 0 ? (
                      <span className="visit-count visit-count--repeat">{cp.customers}</span>
                    ) : (
                      <Empty />
                    )}
                  </td>
                  <td className="cell-muted">{cp.createdBy || <Empty />}</td>
                  <td className="cell-date">{formatDate(cp.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loadError && items.length > 0 && (
        <div className="cp-more">
          <span>
            Showing {items.length.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")}
          </span>
          {items.length < total && (
            <button type="button" className="pv-btn pv-btn--ghost" onClick={() => load({ append: true })} disabled={loading}>
              {loading ? "Loading…" : "Show more"}
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="project-modal-backdrop" onClick={close}>
          <form
            className="project-modal rm-modal cp-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            aria-labelledby="cp-modal-title"
          >
            <button type="button" className="project-modal-close" aria-label="Close" onClick={close} disabled={saving}>
              <MdClose />
            </button>
            <div className="project-modal-preview">
              <ProjectLogo name={form.name.trim() || "Channel Partner"} size={64} className="is-round" />
            </div>
            <h3 id="cp-modal-title">Add a channel partner</h3>
            <p>Registers the broker the same way as the Channel Partner form, credited to you.</p>

            <div className="cp-form-grid">
              <div className="cp-field">
                <label htmlFor="cp-add-name">Partner name *</label>
                <input id="cp-add-name" autoFocus value={form.name} onChange={set("name")} placeholder="Full name" maxLength={80} autoComplete="off" />
              </div>
              <div className="cp-field">
                <label htmlFor="cp-add-phone">Phone *</label>
                <input
                  id="cp-add-phone"
                  type="tel"
                  inputMode="numeric"
                  value={form.phoneNumber}
                  onChange={set("phoneNumber")}
                  placeholder="10-digit mobile"
                  autoComplete="off"
                />
                {form.phoneNumber && form.phoneNumber.length !== 10 && (
                  <small className="rm-field-hint">{10 - form.phoneNumber.length} more digit{10 - form.phoneNumber.length === 1 ? "" : "s"}</small>
                )}
              </div>
              <div className="cp-field cp-field--full">
                <label htmlFor="cp-add-company">Company *</label>
                <input id="cp-add-company" value={form.companyName} onChange={set("companyName")} placeholder="e.g. SS Landbase Realty" maxLength={100} autoComplete="off" />
              </div>
              <div className="cp-field">
                <label htmlFor="cp-add-project">Place of visit *</label>
                <select id="cp-add-project" value={form.placeOfVisit} onChange={set("placeOfVisit")}>
                  <option value="">Choose project</option>
                  {filterProjects.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="cp-field">
                <label htmlFor="cp-add-date">Date</label>
                <input id="cp-add-date" type="date" value={form.date} max={today()} onChange={set("date")} />
              </div>
              <div className="cp-field cp-field--full">
                <label htmlFor="cp-add-meeting">Meeting with</label>
                <select id="cp-add-meeting" value={form.meetingWith} onChange={set("meetingWith")}>
                  <option value="">Not specified</option>
                  {salesNames.map((entry) => (
                    <option key={entry} value={salesPerson(entry)}>{entry}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="project-modal-actions">
              <button type="button" className="pv-btn pv-btn--ghost" onClick={close} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="pv-btn pv-btn--primary" disabled={saving || !formValid}>
                {saving ? "Registering…" : "Register partner"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default BrokersView;
